import React, { useState, useMemo } from 'react';
import { Calendar, DollarSign, Plus, Search, Filter, Loader2, AlertCircle, MoreVertical, Edit, Trash2, Pause, Play } from 'lucide-react';
import SubscriptionFormModal, { SubscriptionFormData } from './SubscriptionFormModal';
import { useDashboardSummary, useSubscriptions, useCategories, useCreateSubscription, useUpdateSubscription, useDeleteSubscription, usePauseSubscription, useResumeSubscription } from '../hooks/useApi';
import { Subscription } from '../lib/api';

// 古いローカル型からAPIの型へのマッピング用のヘルパー関数
const mapSubscriptionToLegacyFormat = (sub: Subscription) => ({
    id: sub.id,
    name: sub.service_name,
    category: sub.category?.name || 'その他',
    nextPayment: sub.next_payment_date,
    amount: sub.price,
    continuationMonths: calculateMonthsActive(sub.initial_date),
    totalPaid: calculateTotalPaid(sub.price, sub.cycle, sub.initial_date),
    isPaused: sub.is_paused,
    pausedDate: sub.paused_date
});

// 継続月数を計算
const calculateMonthsActive = (initialDate: string): number => {
    const start = new Date(initialDate);
    const now = new Date();
    const diffInMonths = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    return Math.max(1, diffInMonths);
};

// 累計支払額を計算
const calculateTotalPaid = (price: number, cycle: string, initialDate: string): number => {
    const monthsActive = calculateMonthsActive(initialDate);
    
    if (cycle === 'monthly') {
        return price * monthsActive;
    } else if (cycle === 'annual') {
        const yearsActive = Math.ceil(monthsActive / 12);
        return price * yearsActive;
    } else if (cycle === 'semiannual') {
        const semiAnnualPeriodsActive = Math.ceil(monthsActive / 6);
        return price * semiAnnualPeriodsActive;
    }
    
    return price;
};

const SubscNaviHome = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState<string>('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortBy, setSortBy] = useState<'name' | 'nextPayment' | 'amount' | 'totalPaid'>('nextPayment');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
        open: boolean;
        subscription: any | null;
    }>({ open: false, subscription: null });
    
    const [pauseConfirmDialog, setPauseConfirmDialog] = useState<{
        open: boolean;
        subscription: any | null;
        action: 'pause' | 'resume';
    }>({ open: false, subscription: null, action: 'pause' });

    // APIからデータを取得
    const { 
        data: summaryData, 
        isLoading: summaryLoading, 
        error: summaryError 
    } = useDashboardSummary();
    
    const { 
        data: subscriptionsData, 
        isLoading: subscriptionsLoading, 
        error: subscriptionsError 
    } = useSubscriptions();
    
    const { 
        data: categoriesData, 
        isLoading: categoriesLoading 
    } = useCategories();

    // サブスクリプション作成のmutation
    const createSubscriptionMutation = useCreateSubscription();
    
    // サブスクリプション更新のmutation
    const updateSubscriptionMutation = useUpdateSubscription();
    
    // サブスクリプション削除のmutation
    const deleteSubscriptionMutation = useDeleteSubscription();
    
    // サブスクリプション一時停止のmutation
    const pauseSubscriptionMutation = usePauseSubscription();
    
    // サブスクリプション再開のmutation
    const resumeSubscriptionMutation = useResumeSubscription();

    // データ変換とフィルタリング
    const subscriptions = useMemo(() => {
        if (!subscriptionsData) return [];
        return subscriptionsData.map(mapSubscriptionToLegacyFormat);
    }, [subscriptionsData]);

    const categories = useMemo(() => {
        const categoryNames = categoriesData?.map(cat => cat.name) || [];
        return ['all', ...categoryNames];
    }, [categoriesData]);

    const filteredAndSortedSubscriptions = useMemo(() => {
        // フィルタリング
        const filtered = subscriptions.filter(sub => {
            const matchesSearch = sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sub.category.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = filterCategory === 'all' || sub.category === filterCategory;
            return matchesSearch && matchesCategory;
        });

        // ソート
        return filtered.sort((a, b) => {
            let comparison = 0;
            
            switch (sortBy) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'nextPayment':
                    comparison = new Date(a.nextPayment).getTime() - new Date(b.nextPayment).getTime();
                    break;
                case 'amount':
                    comparison = a.amount - b.amount;
                    break;
                case 'totalPaid':
                    comparison = a.totalPaid - b.totalPaid;
                    break;
                default:
                    comparison = 0;
            }
            
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [subscriptions, searchTerm, filterCategory, sortBy, sortOrder]);

    // ヘルパー関数
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    const formatCurrency = (amount: number) => {
        return `¥${amount.toLocaleString()}`;
    };

    const getDaysUntilPayment = (dateString: string) => {
        const today = new Date();
        const paymentDate = new Date(dateString);
        const diffTime = paymentDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // ソートハンドラー
    const handleSort = (column: 'name' | 'nextPayment' | 'amount' | 'totalPaid') => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    // ソートアイコンを取得
    const getSortIcon = (column: 'name' | 'nextPayment' | 'amount' | 'totalPaid') => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? '↑' : '↓';
    };

    const handleAddSubscription = async (formData: SubscriptionFormData) => {
        try {
            if (editingSubscription) {
                // 編集モード
                await updateSubscriptionMutation.mutateAsync({
                    id: editingSubscription.id,
                    ...formData
                });
            } else {
                // 新規作成モード
                await createSubscriptionMutation.mutateAsync(formData);
            }
            setIsModalOpen(false);
            setEditingSubscription(null);
        } catch (error) {
            console.error('サブスクリプション処理エラー:', error);
            // エラーハンドリングは必要に応じて追加
        }
    };

    // 編集ボタンのハンドラー
    const handleEditSubscription = (subscription: any) => {
        // ローカル形式からAPIの形式に変換
        const originalSub = subscriptionsData?.find(sub => sub.id === subscription.id);
        if (originalSub) {
            setEditingSubscription(originalSub);
            setIsModalOpen(true);
        }
        setOpenDropdownId(null);
    };

    // 削除ボタンのハンドラー
    const handleDeleteSubscription = (subscription: any) => {
        setDeleteConfirmDialog({ open: true, subscription });
        setOpenDropdownId(null);
    };

    // 削除確認ダイアログでの削除実行
    const confirmDeleteSubscription = async () => {
        if (deleteConfirmDialog.subscription) {
            try {
                await deleteSubscriptionMutation.mutateAsync(deleteConfirmDialog.subscription.id);
                setDeleteConfirmDialog({ open: false, subscription: null });
            } catch (error) {
                console.error('サブスクリプション削除エラー:', error);
                // エラーハンドリングは必要に応じて追加
            }
        }
    };

    // 削除キャンセル
    const cancelDeleteSubscription = () => {
        setDeleteConfirmDialog({ open: false, subscription: null });
    };

    // 一時停止ボタンのハンドラー
    const handlePauseSubscription = (subscription: any) => {
        setPauseConfirmDialog({ open: true, subscription, action: 'pause' });
        setOpenDropdownId(null);
    };

    // 再開ボタンのハンドラー
    const handleResumeSubscription = (subscription: any) => {
        setPauseConfirmDialog({ open: true, subscription, action: 'resume' });
        setOpenDropdownId(null);
    };

    // 一時停止/再開確認ダイアログでの実行
    const confirmPauseResumeSubscription = async () => {
        if (pauseConfirmDialog.subscription) {
            try {
                if (pauseConfirmDialog.action === 'pause') {
                    await pauseSubscriptionMutation.mutateAsync(pauseConfirmDialog.subscription.id);
                } else {
                    await resumeSubscriptionMutation.mutateAsync(pauseConfirmDialog.subscription.id);
                }
                setPauseConfirmDialog({ open: false, subscription: null, action: 'pause' });
            } catch (error) {
                console.error('サブスクリプション一時停止/再開エラー:', error);
                // エラーハンドリングは必要に応じて追加
            }
        }
    };

    // 一時停止/再開キャンセル
    const cancelPauseResumeSubscription = () => {
        setPauseConfirmDialog({ open: false, subscription: null, action: 'pause' });
    };

    // ドロップダウンの表示切り替え
    const toggleDropdown = (subscriptionId: number) => {
        setOpenDropdownId(openDropdownId === subscriptionId ? null : subscriptionId);
    };

    // ドロップダウン外クリック時の処理
    React.useEffect(() => {
        const handleClickOutside = () => {
            setOpenDropdownId(null);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // ローディング状態
    if (summaryLoading || subscriptionsLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="flex items-center space-x-2">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span>データを読み込んでいます...</span>
                </div>
            </div>
        );
    }

    // エラー状態
    if (summaryError || subscriptionsError) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                    <div className="flex items-center space-x-2 text-red-600 mb-4">
                        <AlertCircle className="w-6 h-6" />
                        <h2 className="text-lg font-semibold">データの取得に失敗しました</h2>
                    </div>
                    <p className="text-gray-600 mb-4">
                        サーバーに接続できませんでした。バックエンドが起動していることを確認してください。
                    </p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                    >
                        再試行
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">サブスクナビ</h1>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
                {/* Summary Cards */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6 sm:mb-8">
                    {/* Monthly Total */}
                    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-600">月の支払い合計</h3>
                            <DollarSign className="w-5 h-5 text-green-600" />
                        </div>
                        <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                            {formatCurrency(summaryData?.total_monthly_cost || 0)}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                            {summaryData?.total_subscriptions || 0}つのサービス
                        </p>
                    </div>
                    {/* Annual Total */}
                    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-600">年間予想支払い</h3>
                            <DollarSign className="w-5 h-5 text-purple-600" />
                        </div>
                        <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                            {formatCurrency(summaryData?.total_annual_cost || 0)}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">現在のサービス継続時</p>
                    </div>
                    {/* Next Payment */}
                    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-600">次回支払い</h3>
                            <Calendar className="w-5 h-5 text-blue-600" />
                        </div>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                            {summaryData?.next_payment_service || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                            {summaryData?.next_payment_date ? (
                                <>
                                    {formatDate(summaryData.next_payment_date)}
                                    ({summaryData.next_payment_days}日後)
                                </>
                            ) : (
                                'データなし'
                            )}
                        </p>
                    </div>
                </div>

                {/* Controls */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
                        {/* Search Input */}
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="サービス名で検索..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                            />
                        </div>
                        
                        {/* Controls Row */}
                        <div className="flex gap-3 sm:gap-4">
                            {/* Category Filter */}
                            <div className="relative flex-1 sm:flex-none">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <select
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                    className="w-full sm:w-auto pl-10 pr-8 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white text-gray-900 text-base sm:text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-blue-400"
                                    disabled={categoriesLoading}
                                >
                                    {categories.map(category => (
                                        <option key={category} value={category}>
                                            {category === 'all' ? 'すべて' : category}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Sort Options */}
                            <div className="relative flex-1 sm:flex-none">
                                <select
                                    value={`${sortBy}-${sortOrder}`}
                                    onChange={(e) => {
                                        const [newSortBy, newSortOrder] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
                                        setSortBy(newSortBy);
                                        setSortOrder(newSortOrder);
                                    }}
                                    className="w-full sm:w-auto pl-3 pr-8 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white text-gray-900 text-base sm:text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-blue-400"
                                >
                                    <option value="nextPayment-asc">次回支払日 (昇順)</option>
                                    <option value="nextPayment-desc">次回支払日 (降順)</option>
                                    <option value="amount-asc">金額 (安い順)</option>
                                    <option value="amount-desc">金額 (高い順)</option>
                                    <option value="name-asc">サービス名 (A-Z)</option>
                                    <option value="name-desc">サービス名 (Z-A)</option>
                                    <option value="totalPaid-asc">累計支払額 (安い順)</option>
                                    <option value="totalPaid-desc">累計支払額 (高い順)</option>
                                </select>
                            </div>
                        </div>
                        
                        {/* Add Button */}
                        <button 
                            className="bg-blue-600 text-white px-4 py-2.5 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap text-base sm:text-sm font-medium"
                            onClick={() => setIsModalOpen(true)}
                        >
                            <Plus className="w-4 h-4" />
                            <span>新規追加</span>
                        </button>
                    </div>
                </div>

                {/* Subscriptions Table/Cards */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th 
                                        className="text-left py-3 px-6 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('name')}
                                    >
                                        サービス名 {getSortIcon('name')}
                                    </th>
                                    <th 
                                        className="text-left py-3 px-6 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('nextPayment')}
                                    >
                                        次回支払日 {getSortIcon('nextPayment')}
                                    </th>
                                    <th 
                                        className="text-left py-3 px-6 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('amount')}
                                    >
                                        金額 {getSortIcon('amount')}
                                    </th>
                                    <th className="text-left py-3 px-6 font-medium text-gray-600">継続月数</th>
                                    <th 
                                        className="text-left py-3 px-6 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('totalPaid')}
                                    >
                                        累計支払額 {getSortIcon('totalPaid')}
                                    </th>
                                    <th className="text-left py-3 px-6 font-medium text-gray-600">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedSubscriptions.map((subscription) => (
                                    <tr key={subscription.id} className={`border-b hover:bg-gray-50 transition-colors ${subscription.isPaused ? 'bg-gray-100 opacity-75' : ''}`}>
                                        <td className="py-4 px-6">
                                            <div>
                                                <div className={`font-medium flex items-center gap-2 ${subscription.isPaused ? 'text-gray-500' : 'text-gray-900'}`}>
                                                    {subscription.name}
                                                    {subscription.isPaused && (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                                            <Pause className="w-3 h-3 mr-1" />
                                                            停止中
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-500">{subscription.category}</div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            {subscription.isPaused ? (
                                                <div className="text-gray-500">停止中</div>
                                            ) : (
                                                <>
                                                    <div className="text-gray-900">{formatDate(subscription.nextPayment)}</div>
                                                    <div className="text-sm text-gray-500">
                                                        {getDaysUntilPayment(subscription.nextPayment)}日後
                                                    </div>
                                                </>
                                            )}
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className={`font-medium ${subscription.isPaused ? 'text-gray-500' : 'text-gray-900'}`}>
                                                {formatCurrency(subscription.amount)}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className={subscription.isPaused ? 'text-gray-500' : 'text-gray-900'}>{subscription.continuationMonths}ヶ月</span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className="font-medium text-gray-900">
                                                {formatCurrency(subscription.totalPaid)}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleDropdown(subscription.id);
                                                    }}
                                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                                >
                                                    <MoreVertical className="w-4 h-4 text-gray-500" />
                                                </button>
                                                
                                                {openDropdownId === subscription.id && (
                                                    <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-md shadow-lg min-w-[120px]">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditSubscription(subscription);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                            <span>編集</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteSubscription(subscription);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            <span>削除</span>
                                                        </button>
                                                        {subscription.isPaused ? (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleResumeSubscription(subscription);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                                                            >
                                                                <Play className="w-4 h-4" />
                                                                <span>再開</span>
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePauseSubscription(subscription);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50 flex items-center space-x-2"
                                                            >
                                                                <Pause className="w-4 h-4" />
                                                                <span>一時停止</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="sm:hidden">
                        {filteredAndSortedSubscriptions.map((subscription) => (
                            <div key={subscription.id} className={`border-b last:border-b-0 p-4 ${subscription.isPaused ? 'bg-gray-100 opacity-75' : ''}`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className={`font-medium text-lg ${subscription.isPaused ? 'text-gray-500' : 'text-gray-900'}`}>{subscription.name}</h3>
                                            {subscription.isPaused && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                                    <Pause className="w-3 h-3 mr-1" />
                                                    停止中
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500">{subscription.category}</p>
                                    </div>
                                    <div className="relative ml-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleDropdown(subscription.id);
                                            }}
                                            className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                                        >
                                            <MoreVertical className="w-5 h-5 text-gray-500" />
                                        </button>
                                        
                                        {openDropdownId === subscription.id && (
                                            <div className="absolute right-0 top-10 z-10 bg-white border border-gray-200 rounded-md shadow-lg min-w-[120px]">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditSubscription(subscription);
                                                    }}
                                                    className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-100 flex items-center space-x-2 touch-manipulation"
                                                >
                                                    <Edit className="w-5 h-5" />
                                                    <span>編集</span>
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSubscription(subscription);
                                                    }}
                                                    className="w-full text-left px-4 py-3 text-base text-red-600 hover:bg-red-50 flex items-center space-x-2 touch-manipulation"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                    <span>削除</span>
                                                </button>
                                                {subscription.isPaused ? (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleResumeSubscription(subscription);
                                                        }}
                                                        className="w-full text-left px-4 py-3 text-base text-green-600 hover:bg-green-50 flex items-center space-x-2 touch-manipulation"
                                                    >
                                                        <Play className="w-5 h-5" />
                                                        <span>再開</span>
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePauseSubscription(subscription);
                                                        }}
                                                        className="w-full text-left px-4 py-3 text-base text-yellow-600 hover:bg-yellow-50 flex items-center space-x-2 touch-manipulation"
                                                    >
                                                        <Pause className="w-5 h-5" />
                                                        <span>一時停止</span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-gray-500">次回支払日</span>
                                        {subscription.isPaused ? (
                                            <div className="font-medium text-gray-500">停止中</div>
                                        ) : (
                                            <>
                                                <div className="font-medium text-gray-900">{formatDate(subscription.nextPayment)}</div>
                                                <div className="text-xs text-gray-500">
                                                    {getDaysUntilPayment(subscription.nextPayment)}日後
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div>
                                        <span className="text-gray-500">金額</span>
                                        <div className={`font-medium text-lg ${subscription.isPaused ? 'text-gray-500' : 'text-gray-900'}`}>
                                            {formatCurrency(subscription.amount)}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">継続月数</span>
                                        <div className={`font-medium ${subscription.isPaused ? 'text-gray-500' : 'text-gray-900'}`}>{subscription.continuationMonths}ヶ月</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">累計支払額</span>
                                        <div className={`font-medium ${subscription.isPaused ? 'text-gray-500' : 'text-gray-900'}`}>
                                            {formatCurrency(subscription.totalPaid)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {filteredAndSortedSubscriptions.length === 0 && !subscriptionsLoading && (
                    <div className="text-center py-12">
                        <p className="text-gray-500">
                            {subscriptions.length === 0 
                                ? 'サブスクリプションが登録されていません。' 
                                : '該当するサブスクリプションが見つかりません。'
                            }
                        </p>
                    </div>
                )}
            </main>

            {/* Subscription Form Modal */}
            <SubscriptionFormModal
                open={isModalOpen}
                onOpenChange={(open) => {
                    setIsModalOpen(open);
                    if (!open) {
                        setEditingSubscription(null);
                    }
                }}
                onSubmit={handleAddSubscription}
                isLoading={createSubscriptionMutation.isPending || updateSubscriptionMutation.isPending}
                mode={editingSubscription ? 'edit' : 'create'}
                editData={editingSubscription ? {
                    id: editingSubscription.id,
                    service_name: editingSubscription.service_name,
                    price: editingSubscription.price,
                    cycle: editingSubscription.cycle,
                    initial_date: editingSubscription.initial_date,
                    next_payment_date: editingSubscription.next_payment_date,
                    payment_method: editingSubscription.payment_method,
                    category_id: editingSubscription.category_id,
                    memo: editingSubscription.memo
                } : undefined}
            />

            {/* Delete Confirmation Dialog */}
            {deleteConfirmDialog.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            サブスクリプションを削除
                        </h3>
                        <p className="text-gray-600 mb-6">
                            「{deleteConfirmDialog.subscription?.name}」を削除しますか？
                            この操作は取り消せません。
                        </p>
                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                            <button
                                onClick={cancelDeleteSubscription}
                                className="px-4 py-2.5 sm:py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-base sm:text-sm font-medium"
                                disabled={deleteSubscriptionMutation.isPending}
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={confirmDeleteSubscription}
                                className="px-4 py-2.5 sm:py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 text-base sm:text-sm font-medium"
                                disabled={deleteSubscriptionMutation.isPending}
                            >
                                {deleteSubscriptionMutation.isPending ? '削除中...' : '削除'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pause/Resume Confirmation Dialog */}
            {pauseConfirmDialog.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            サブスクリプションの{pauseConfirmDialog.action === 'pause' ? '一時停止' : '再開'}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            「{pauseConfirmDialog.subscription?.name}」を{pauseConfirmDialog.action === 'pause' ? '一時停止' : '再開'}しますか？
                            この操作は取り消せません。
                        </p>
                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                            <button
                                onClick={cancelPauseResumeSubscription}
                                className="px-4 py-2.5 sm:py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-base sm:text-sm font-medium"
                                disabled={pauseSubscriptionMutation.isPending || resumeSubscriptionMutation.isPending}
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={confirmPauseResumeSubscription}
                                className="px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 text-base sm:text-sm font-medium"
                                disabled={pauseSubscriptionMutation.isPending || resumeSubscriptionMutation.isPending}
                            >
                                {pauseConfirmDialog.action === 'pause' ? (pauseSubscriptionMutation.isPending ? '一時停止中...' : '一時停止') : (resumeSubscriptionMutation.isPending ? '再開中...' : '再開')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscNaviHome;
